/**
 * Publicité récompensée — le fournisseur (demande de Benoit, 18 août 2026).
 *
 * ══ La vraie régie, et son repli honnête ═════════════════════════════════════
 *
 * La régie est la **Google Ad Placement API** (H5 Games Ads) : le script officiel
 * `adsbygoogle`, appelé par `adBreak({ type: 'reward' })`. Elle est BRANCHÉE — mais
 * une régie exige un identifiant d'éditeur (`ca-pub-…`), qui n'existe qu'avec le
 * compte AdSense de Benoit. Tant que `VITE_ADSENSE_CLIENT` est vide :
 *
 * - le script officiel n'est pas chargé (aucun octet de pub sur la page) ;
 * - un visionnage SIMULÉ prend sa place — un compte à rebours plein écran, marqué
 *   comme test — pour que le flux entier (bouton → visionnage → récompense →
 *   plafond quotidien) soit réel, mesurable, et sondé dès aujourd'hui.
 *
 * Le jour où l'identifiant arrive : une variable d'environnement, zéro code.
 *
 * ══ Contrat ═════════════════════════════════════════════════════════════════
 *
 * `showRewardedAd()` résout l'une de TROIS issues, et la distinction n'est pas
 * cosmétique : « tu as fermé la pub » et « aucune pub n'était disponible » ne
 * demandent pas la même chose au joueur, et confondre les deux revient à
 * l'accuser d'un défaut de la régie. L'appelant ne récompense que sur
 * `'rewarded'` — c'est le contrat des rewarded ads, et c'est lui qui protège
 * l'économie.
 */

export type AdOutcome =
  /** Vue jusqu'au bout : la récompense est due. */
  | 'rewarded'
  /** Le joueur l'a fermée avant la fin : rien n'est dû, et il le sait. */
  | 'dismissed'
  /** Rien à servir — pas de remplissage, script bloqué, régie muette. */
  | 'unavailable';

import { FEATURES } from '@/config/features';

const CLIENT_ID: string =
  typeof import.meta.env.VITE_ADSENSE_CLIENT === 'string'
    ? import.meta.env.VITE_ADSENSE_CLIENT
    : '';

/** Durée du visionnage simulé. Courte mais pas instantanée : le flux se ressent. */
const SIMULATED_MS = 5000;

/**
 * Délai au-delà duquel une annonce qui n'a pas COMMENCÉ est déclarée indisponible.
 *
 * Huit secondes : au-delà, la régie n'a rien à servir, et faire patienter plus
 * longtemps devant un écran immobile est pire que de le dire tout de suite.
 */
const START_TIMEOUT_MS = 8000;

type AdBreak = (options: {
  type: string;
  name: string;
  beforeReward?: (showAdFn: () => void) => void;
  adViewed?: () => void;
  adDismissed?: () => void;
  adBreakDone?: (info: unknown) => void;
}) => void;

let scriptLoaded = false;

/** Charge le script officiel, UNE fois, à la demande — jamais au démarrage (E§3). */
function loadAdScript(): Promise<AdBreak | null> {
  return new Promise((resolve) => {
    const existing = (globalThis as { adBreak?: AdBreak }).adBreak;
    if (scriptLoaded && existing !== undefined) {
      resolve(existing);
      return;
    }

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${CLIENT_ID}`;
    script.crossOrigin = 'anonymous';
    script.dataset.adFrequencyHint = '120s';
    script.onload = () => {
      scriptLoaded = true;
      // La file `adsbygoogle` expose `adBreak` après l'initialisation H5.
      const w = globalThis as { adsbygoogle?: unknown[]; adBreak?: AdBreak };
      w.adsbygoogle = w.adsbygoogle ?? [];
      w.adsbygoogle.push({
        preloadAdBreaks: 'on',
        sound: 'on',
        onReady: () => {
          resolve((globalThis as { adBreak?: AdBreak }).adBreak ?? null);
        },
      });
      // Si `onReady` ne vient jamais (réseau, blocage), on rend la main — borné.
      globalThis.setTimeout(() => {
        resolve((globalThis as { adBreak?: AdBreak }).adBreak ?? null);
      }, 6000);
    };
    script.onerror = () => {
      resolve(null);
    };
    document.head.append(script);
  });
}

/** Le visionnage simulé : un voile plein écran, un compte à rebours, marqué test. */
function showSimulated(): Promise<AdOutcome> {
  return new Promise((resolve) => {
    const veil = document.createElement('div');
    veil.className = 'ad-simulated';
    veil.setAttribute('role', 'dialog');

    const label = document.createElement('p');
    label.className = 'ad-simulated__label';

    const note = document.createElement('p');
    note.className = 'ad-simulated__note';
    note.textContent = 'TEST';

    veil.append(note, label);
    document.body.append(veil);

    const startedAt = performance.now();
    const tick = (): void => {
      const left = Math.max(0, SIMULATED_MS - (performance.now() - startedAt));
      label.textContent = String(Math.ceil(left / 1000));
      if (left > 0) {
        globalThis.requestAnimationFrame(tick);
      } else {
        veil.remove();
        resolve('rewarded');
      }
    };
    tick();
  });
}

/**
 * Montre une publicité récompensée et dit CE QUI s'est passé.
 *
 * Ne rejette jamais : un flux de récompense qui jette laisse le joueur sans
 * réponse. Une pub indisponible est une issue, pas une panne.
 */
export async function showRewardedAd(): Promise<AdOutcome> {
  /*
    ⚠️ LE DRAPEAU D'ABORD, l'identifiant ensuite — et cet ordre est un correctif.

    Ce test portait sur le seul `CLIENT_ID`. Or `VITE_ADSENSE_CLIENT` **est renseigné
    côté Cloudflare Pages** et pas dans le `.env` du poste : en production, le bouton
    partait donc vers une régie qui n'a jamais approuvé le site, ne remplissait rien, et
    rendait « indisponible ». Le joueur cliquait « Regarder une pub » et n'obtenait
    aucune pub — pendant que le visionnage simulé marchait parfaitement en local.

    Mesuré le 19 août par le parcours enchaîné lancé contre la production : 62/64, les
    deux seuls rouges étant ces deux contrôles-là, verts en local. Aucun test local ne
    pouvait le voir, la variable n'existant que côté hébergeur.

    `FEATURES.rewardedAds` disait déjà « la régie n'est pas en service » — le code ne le
    lisait simplement jamais. Tant qu'il est coupé, on simule, quel que soit
    l'identifiant présent : un drapeau qu'aucun code ne consulte n'est pas un drapeau.
  */
  if (!FEATURES.rewardedAds || CLIENT_ID === '') return showSimulated();

  const adBreak = await loadAdScript();
  // Script bloqué (extension), réseau coupé, régie absente : rien à servir.
  if (adBreak === null) return 'unavailable';

  return new Promise((resolve) => {
    let rewarded = false;
    let started = false;
    let settled = false;

    const finish = (value: AdOutcome): void => {
      if (settled) return;
      settled = true;
      resolve(value);
    };

    /*
      ⚠️ BORNE SUR LE DÉMARRAGE DE LA PUB, pas sur son visionnage.

      Rien ne garantit que `adBreakDone` arrive : tant que le site n'est pas approuvé
      par AdSense, ou simplement quand aucune annonce ne se remplit, la régie peut
      rester muette. La promesse ne se résolvait alors JAMAIS — le joueur cliquait
      « Regarder une pub » et n'obtenait ni pub, ni bonus, ni explication. Un silence
      est le pire retour possible ; c'est exactement ce qui rendait une vraie régie
      non remplie pire que le visionnage simulé.

      La borne ne peut pas porter sur la durée totale : une vidéo récompensée dure
      couramment trente secondes, et couper au milieu volerait sa récompense au
      joueur. Elle porte donc sur le DÉMARRAGE — `beforeReward` signale qu'une
      annonce existe et va se jouer, et désarme le compte à rebours. Après lui, on
      laisse la pub aller à son terme, aussi longue soit-elle.
    */
    const startDeadline = globalThis.setTimeout(() => {
      finish('unavailable');
    }, START_TIMEOUT_MS);

    adBreak({
      type: 'reward',
      name: 'production-boost',
      beforeReward: (showAdFn) => {
        // Une annonce est prête : elle a le droit de durer.
        started = true;
        globalThis.clearTimeout(startDeadline);
        showAdFn();
      },
      adViewed: () => {
        rewarded = true;
      },
      adDismissed: () => {
        rewarded = false;
      },
      adBreakDone: () => {
        globalThis.clearTimeout(startDeadline);
        /*
          Fini SANS qu'aucune annonce n'ait commencé : il n'y avait rien à servir.
          Le dire « interrompue » accuserait le joueur d'un geste qu'il n'a pas fait.
        */
        finish(rewarded ? 'rewarded' : started ? 'dismissed' : 'unavailable');
      },
    });
  });
}

/** Le fournisseur est-il en mode simulé ? Lu par l'interface pour l'annoncer. */
export function isSimulatedAdProvider(): boolean {
  return CLIENT_ID === '';
}
